<template>
  <v-form
    ref="form"
    :disabled="loader.loading"
  >
    <v-row>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.name"
          variant="outlined"
          label="Nome"
          :rules="[required]"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="location"
          :items="locations"
          item-value="id"
          :item-title="l => `${l.name} - ${l.id.split('-')[0]}`"
          variant="outlined"
          label="Local"
          :rules="[required]"
          return-object
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-file-input
          v-model="image"
          label="Imagem (formato SVG)"
          accept=".svg,image/svg+xml"
          variant="outlined"
          prepend-inner-icon="fa fa-paperclip"
          prepend-icon=""
          append-inner-icon="fa fa-eye"
          @click:append="onPreview = !onPreview"
          @change="onFileChange"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-tooltip :disabled="!saveBlocker" :text="saveBlocker ?? ''" location="bottom">
          <template #activator="{ props: tip }">
            <div v-bind="tip">
              <v-btn
                color="primary"
                block
                :loading="loader.loading"
                :disabled="!!saveBlocker"
                size="x-large"
                @click="submit"
              >
                {{ !!model.id ? 'Salvar' : 'Criar' }}
              </v-btn>
            </div>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-switch
          v-model="model.has_sector_selection"
          color="primary"
          label="Seleção de setor no mapa"
          hint="O comprador escolhe o setor (e a seção) clicando no mapa antes de escolher os ingressos ou lugares"
          persistent-hint
          inset
        />
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-dialog
        v-model="onPreview"
        scrollable
        max-width="650px"
      >
        <v-card>
          <v-toolbar
            color="primary"
            dark
            class="pl-6"
          >
            <span class="text-h6 text-center">
              Preview da Imagem
            </span>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <div class="d-flex">
                  <v-img :src="imagePreview" max-height="170" />
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, useTemplateRef, watch, watchEffect } from 'vue'
import Mapping, { MappingPayload } from '@/types/Mapping'
import LocalService from '@/services/LocalService'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import Local from '@/types/Local'
import { required } from '@/helpers/rules/required'
import type { VForm } from 'vuetify/lib/components'

const snackbar = useSnackBar()
const loader = useLoader()

const emit = defineEmits<{
  'submit': [ MappingPayload ]
  'image-change': [ File | null ]
}>()

/** Why Salvar is disabled, shown as its tooltip */
const { saveBlocker = null } = defineProps<{ saveBlocker?: string | null }>()

const model = defineModel<Mapping>({ required: true })

const form = useTemplateRef<VForm>('form')

const onPreview = ref(false)

const image = ref<File | null>(null)

const imagePreview = computed(() => {
  if (!model.value.image_url && !image.value) return '/src/assets/images/logo.png'
  if (image.value) return URL.createObjectURL(image.value)
  return model.value.image_url
})

const location = ref<Local>()

async function submit() {
  await form.value?.validate()
  if (!form.value?.isValid || !location.value) {
    snackbar.show(
      'Verifique se os campos foram preenchidos corretamente',
      'warning',
    )
    return
  }

  const payload: MappingPayload = {
    image: image.value ?? null,
    name: model.value.name,
    locationId: location.value.id,
    hasSectorSelection: model.value.has_sector_selection,
    sectors: model.value.sectors ?? [],
  }
  emit('submit', payload)
}

const locations = ref<Local[]>([])

onBeforeMount(async () => await fetchLocations())

async function fetchLocations() {
  try {
    loader.increment()
    const response = await (new LocalService()).index(1, ``, 100)
    locations.value = response.data.data
      .filter(l => l.active)
      .sort((a, b) => a.name.localeCompare(b.name))

    if (model.value.id) {
      location.value = model.value.local
    }
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os Locais',
      'error',
    )
    throw error
  } finally {
    loader.decrement()
  }
}

function onFileChange(event: Event) {
  const files = (event.target as HTMLInputElement).files
  if (files && files.length > 0) {
    image.value = files[0]
  }
}

// Clearing the input only updates the v-model, without a change event
watch(image, file => emit('image-change', file instanceof File ? file : null))

watchEffect(() => {
  if (model.value.local?.id) {
    location.value = model.value.local
  }
})

</script>
